import type { UserNotificationType } from "common/types/userNotification";
import type { AnalyticsEventMetadata } from "lib/analytics";
import type { ExportPlanEvent } from "lib/components/builders/communities/utils/exportPlanTypes";
import type { PDFWorkerError } from "lib/workers/pdfWorkerMessages";
import type { MutableRefObject } from "react";
import { useReducer } from "react";

interface State {
  error: PDFWorkerError | null;
  progress: number;
  confirm: boolean;
  downloading: boolean;
  safari: boolean;
}

type DownloadActionTypes =
  | { type: "SET_ERROR"; payload: State["error"] }
  | { type: "SET_PROGRESS"; payload: State["progress"] }
  | { type: "SET_CONFIRM"; payload: State["confirm"] }
  | { type: "SET_DOWNLOADING"; payload: State["downloading"] }
  | { type: "SET_SAFARI"; payload: State["safari"] }
  | { type: "RESET" };

interface DownloadInitParams<T> {
  worker: MutableRefObject<Worker | null>;
  downloadParams: T;
  setUserNotification: (notification: UserNotificationType) => void;
  onProgress: (progress: State["progress"]) => void;
  onClose: () => void;
  onError: (error: PDFWorkerError) => void;
  safari?: boolean;
  sendAnalytics?: (eventName: ExportPlanEvent, metadata?: AnalyticsEventMetadata) => void;
}
export type DownloadInitializer<T> = (params: DownloadInitParams<T>) => Promise<(() => void) | void>;

const initialState: State = {
  error: null,
  progress: 0,
  confirm: false,
  downloading: false,
  safari: false,
};

function reducer(state = initialState, action: DownloadActionTypes): State {
  switch (action.type) {
    case "SET_ERROR":
      return { ...initialState, safari: state.safari, error: action.payload };
    case "SET_PROGRESS":
      return { ...state, progress: action.payload };
    case "SET_CONFIRM":
      return { ...state, confirm: action.payload };
    case "SET_DOWNLOADING":
      return { ...state, downloading: action.payload };
    case "SET_SAFARI":
      return { ...state, safari: action.payload };
    case "RESET":
      return { ...initialState, safari: state.safari };
    default:
      return state;
  }
}

export default function useDownloadState() {
  return useReducer(reducer, initialState);
}
